// src/components/bot/contextBuilder.js
// ─────────────────────────────────────────────────────────────────────────────
// Layer 6 — Context Builder
//
// Responsibilities:
//   • Receive the raw API response from the Executor.
//   • Detect the response shape (array of records, single stat object, etc.).
//   • Extract the most meaningful fields.
//   • Serialize into compact, readable plain text.
//   • Clip output to MAX_CONTEXT_CHARS to stay within Gemini's token budget.
//
// Previously, shape-guessing logic was scattered across:
//   • formatGenericResponse() in InternalBotV2
//   • Individual resolve() functions in InternalBot
// It now lives in exactly one place, tested independently from the UI.
// ─────────────────────────────────────────────────────────────────────────────

const MAX_CONTEXT_CHARS = 1800;
const MAX_RECORDS       = 20;  // maximum individual rows to include
const MAX_FIELDS        = 7;   // maximum fields per record

// ── Internal helpers ──────────────────────────────────────────────────────────

/**
 * Extract a flat list of records from an API response.
 * Handles the main response shapes used in this backend:
 *   - Plain array
 *   - { data: [] }
 *   - { json: [] }
 *   - { employees: [], cases: [], vaccinations: [], ... } (first array found)
 *
 * @returns {Array | null}
 */
function extractRecords(data) {
  if (!data) return null;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.json)) return data.json;

  // Scan for first array property (e.g. data.employees, data.vaccinations)
  for (const key of Object.keys(data)) {
    if (Array.isArray(data[key]) && data[key].length > 0) {
      return data[key];
    }
  }

  return null;
}

/**
 * Serialize one record object into a compact "key: value | key: value" string.
 * Skips internal ID fields and nested objects/arrays.
 *
 * @param {object} obj
 * @returns {string}
 */
function serializeRecord(obj) {
  const keys = Object.keys(obj).filter(k =>
    // Skip internal IDs
    k !== 'id' &&
    !k.endsWith('_id') &&
    !k.endsWith('Id') &&
    // Skip nulls and nested objects
    obj[k] !== null &&
    obj[k] !== undefined &&
    typeof obj[k] !== 'object'
  );

  return keys
    .slice(0, MAX_FIELDS)
    .map(k => {
      const label = k.replace(/_/g, ' ');
      const val   = typeof obj[k] === 'number' ? obj[k].toLocaleString() : obj[k];
      return `${label}: ${val}`;
    })
    .join(' | ');
}

/**
 * Serialize a single stats/summary object (e.g. attendance summary, dashboard KPIs).
 *
 * @param {object} obj
 * @returns {string}
 */
function serializeStatsObject(obj) {
  const flat = (obj.data && typeof obj.data === 'object' && !Array.isArray(obj.data))
    ? obj.data
    : obj;

  const keys = Object.keys(flat).filter(k =>
    k !== 'success' &&
    !k.endsWith('_id') &&
    flat[k] !== null &&
    flat[k] !== undefined &&
    typeof flat[k] !== 'object'
  );

  return keys
    .map(k => {
      const label = k.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim();
      const val   = typeof flat[k] === 'number' ? flat[k].toLocaleString() : flat[k];
      return `${label}: ${val}`;
    })
    .join('\n');
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Build a serialized plain-text block from an API response.
 *
 * This text is injected into the Gemini prompt as "[Live data retrieved from VAHD backend]".
 * Gemini then uses it to generate a natural language response.
 *
 * @param {any}    apiData       - Raw data from the API Executor
 * @param {string} endpointLabel - Human-readable name of the endpoint used
 * @returns {string}
 */
export function build(apiData, endpointLabel = 'backend') {
  if (!apiData) return '(no data returned)';

  const lines = [`Source: ${endpointLabel}`];

  // ── Case 1: Array of records ─────────────────────────────────────────────
  const records = extractRecords(apiData);

  if (records && records.length > 0) {
    const total = apiData.total ?? apiData.count ?? apiData.pagination?.total ?? null;
    if (total !== null) {
      lines.push(`Total records: ${Number(total).toLocaleString()}`);
    }
    const shown = Math.min(records.length, MAX_RECORDS);
    lines.push(`Showing ${shown} of ${records.length} entries:`);

    records.slice(0, MAX_RECORDS).forEach((r, i) => {
      const line = typeof r === 'object' && r !== null
        ? serializeRecord(r)
        : String(r);
      lines.push(`${i + 1}. ${line || '(empty record)'}`);
    });

  // ── Case 2: Single stats/summary object ──────────────────────────────────
  } else if (apiData && typeof apiData === 'object' && !Array.isArray(apiData)) {
    const stats = serializeStatsObject(apiData);
    if (stats) {
      lines.push(stats);
    } else {
      // Last resort: JSON slice
      lines.push(JSON.stringify(apiData).slice(0, 500));
    }

  // ── Case 3: Primitive ────────────────────────────────────────────────────
  } else {
    lines.push(String(apiData).slice(0, 500));
  }

  // ── Clip to token budget ─────────────────────────────────────────────────
  let result = lines.join('\n');
  if (result.length > MAX_CONTEXT_CHARS) {
    result = result.slice(0, MAX_CONTEXT_CHARS) + '\n...(truncated for length)';
  }

  return result;
}
